// =============================================================================
// NewProjectModal/index.tsx — Patch 009.2 create-project flow.
// Patch 010.A.4 R6: decomposed from monolithic NewProjectModal.tsx into
// orchestrator + ProjectFieldGroup + ClientFieldGroup + helpers.
//
// Opens from Header's "+ Add Project" button. Mirrors Track 1's
// NewProjectModal shape minus the phase-conditional financial fields
// (those land alongside Schedule / Money surfaces in 010+).
//
// On success:
//   - calls onCreated with the new project id
//   - parent (Header) refreshes the project switcher's project list
//   - Header navigates to /projects/<new-id>/setup (Patch 015.3 — a new
//     project lands on the setup wizard / wizardized intake)
// =============================================================================

import { useEffect, useMemo, useRef, useState } from 'react'

import { Modal } from '../Modal'
import { api, type CreateProjectInput, type CreateProjectResponse } from '../../lib/api'
import { ClientFieldGroup } from './ClientFieldGroup'
import { ProjectFieldGroup } from './ProjectFieldGroup'
import { EMAIL_RE, EMPTY, ZIP_RE, type Draft } from './helpers'

export function NewProjectModal(props: {
  open: boolean
  onClose: () => void
  onCreated: (project: CreateProjectResponse['project']) => void
}): JSX.Element {
  const [d, setD] = useState<Draft>(EMPTY)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const initialFocusRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (props.open) {
      setD(EMPTY)
      setError(null)
      setBusy(false)
      requestAnimationFrame(() => initialFocusRef.current?.focus())
    }
  }, [props.open])

  function update<K extends keyof Draft>(k: K, v: Draft[K]): void {
    setD((prev) => ({ ...prev, [k]: v }))
  }

  const autoName = useMemo(() => {
    const last1 = d.client_last_name.trim()
    const last2 = d.has_client2 ? d.client2_last_name.trim() : ''
    if (last1 && last2) return `${last1} / ${last2} Build`
    if (last1) return `${last1} Build`
    return ''
  }, [d.client_last_name, d.has_client2, d.client2_last_name])
  const effectiveName = d.name_override ?? autoName

  function validate(): string | null {
    if (!d.address.trim()) return 'Address is required'
    if (!d.city.trim()) return 'City is required'
    if (!d.state) return 'State is required'
    if (!d.zip.trim()) return 'ZIP is required'
    if (!ZIP_RE.test(d.zip.trim())) return 'ZIP must be 5 or 5+4 digits'
    if (!d.client_first_name.trim() || !d.client_last_name.trim()) {
      return 'Primary client first + last name required'
    }
    if (d.client_email.trim() && !EMAIL_RE.test(d.client_email.trim())) {
      return 'Primary client email format is invalid'
    }
    if (d.has_client2) {
      if (d.client2_email.trim() && !EMAIL_RE.test(d.client2_email.trim())) {
        return 'Secondary client email format is invalid'
      }
    }
    return null
  }

  async function submit(): Promise<void> {
    const v = validate()
    if (v) {
      setError(v)
      return
    }
    setBusy(true)
    setError(null)
    try {
      const body: CreateProjectInput = {
        address: d.address.trim(),
        city: d.city.trim(),
        state: d.state,
        zip: d.zip.trim(),
        phase: d.phase,
        name: d.name_override ?? null,
        client_first_name: d.client_first_name.trim(),
        client_last_name: d.client_last_name.trim(),
        client_email: d.client_email.trim() || null,
        client_phone: d.client_phone.trim() || null,
        has_client2: d.has_client2,
        client2_first_name: d.has_client2 ? d.client2_first_name.trim() || null : null,
        client2_last_name: d.has_client2 ? d.client2_last_name.trim() || null : null,
        client2_email: d.has_client2 ? d.client2_email.trim() || null : null,
        client2_phone: d.has_client2 ? d.client2_phone.trim() || null : null,
      }
      const result = await api.projects.create(body)
      // Patch 015.40-W1.2 -- fire autopop immediately after create so
      // the project's facts table is populated before the operator
      // hits the wizard's Review-facts step. Without this, every new
      // project lands at Step 2 (Review facts) with an empty list +
      // no way to recover from inside the wizard (the 'Set address'
      // step that handled this case was killed in the same patch).
      // Failure here is non-blocking -- the create itself succeeded;
      // operator can re-fire autopop manually from FactsPage later.
      try {
        await api.autopop.run(result.project.id, {
          street: body.address,
          city: body.city,
          state: body.state,
          zip: body.zip,
        })
      } catch {
        /* non-fatal -- project exists; facts can be populated later */
      }
      props.onCreated(result.project)
      props.onClose()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={props.open}
      title="New project"
      onClose={props.onClose}
      testId="new-project-modal"
      footer={
        <>
          <button
            type="button"
            className="hcc-btn"
            onClick={props.onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="hcc-btn hcc-btn--primary"
            onClick={() => void submit()}
            disabled={busy}
            data-testid="new-project-submit"
          >
            {busy ? 'Creating…' : 'Create project'}
          </button>
        </>
      }
    >
      <ProjectFieldGroup
        draft={d}
        update={update}
        initialFocusRef={initialFocusRef}
      />

      <h3 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>Primary client</h3>
      <ClientFieldGroup draft={d} update={update} prefix="client" primaryTestIds />

      {!d.has_client2 ? (
        <button
          type="button"
          className="hcc-link"
          onClick={() => update('has_client2', true)}
          data-testid="new-project-add-second-client"
          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
        >
          + Add second client
        </button>
      ) : (
        <>
          <h3 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>
            Secondary client{' '}
            <button
              type="button"
              className="hcc-link"
              onClick={() => update('has_client2', false)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: 'normal',
              }}
            >
              (remove)
            </button>
          </h3>
          <ClientFieldGroup draft={d} update={update} prefix="client2" />
        </>
      )}

      {effectiveName ? (
        <p
          className="hcc-muted"
          style={{ marginTop: '0.75rem', fontSize: 'var(--hcc-font-size-small)' }}
        >
          Project name will be{' '}
          <strong style={{ color: 'var(--hcc-gold)' }}>{effectiveName}</strong>{' '}
          (auto-derived; you can rename later).
        </p>
      ) : null}

      {error ? (
        <p
          className="hcc-error"
          data-testid="new-project-error"
          style={{ marginTop: '0.75rem' }}
        >
          {error}
        </p>
      ) : null}
    </Modal>
  )
}
